/**
 * AGENT_BOILERPLATE.js
 *
 * Modular headers and footers for agent code templates across arcade games.
 * User templates show ONLY core logic functions; headers and footers are
 * injected automatically at submission time.
 */
// ============================================================================
// SHARED HEADERS (all games need similar imports + game setup)
// ============================================================================

const SHARED_HEADER = `import requests
import os

GM_HOST = os.environ.get("GM_HOST", "127.0.0.1")
GM_PORT = os.environ.get("GM_PORT", "999999")
GAME_URL = f"http://{GM_HOST}:{GM_PORT}"
`;

// ============================================================================
// GAME-SPECIFIC BOILERPLATES
// ============================================================================

const GAME_BOILERPLATE = {
  // =========================================================================
  // BATTLESHIP
  // =========================================================================
  battleship: {
    header: SHARED_HEADER,
    template: `def choose_target(visible_board):
    """
    Decide the next target to fire at.

    Args:
        visible_board (list): 10x10 grid where:
          '~' = unknown, 'M' = miss, 'H' = hit, 'S' = sunk

    Returns:
        tuple: (row, col) to fire at
    """
    for row in range(len(visible_board)):
        for col in range(len(visible_board[row])):
            if visible_board[row][col] == "~":
                return (row, col)
    return (0, 0)
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over"):
            break

        board = info.get("visible_board", [])
        row, col = choose_target(board)
        requests.post(f"{GAME_URL}/fire", json={"row": row, "col": col}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // CONNECT_FOUR
  // =========================================================================
  connect_four: {
    header: SHARED_HEADER,
    template: `def choose_column(board, current_player, available_columns):
    """
    Decide which column to drop your piece into.

    Args:
        board (list):            6x7 grid (rows x columns)
        current_player (str):    'R' (red) or 'Y' (yellow)
        available_columns (list): column indices that are not full

    Returns:
        int: column index 0-6 to drop into
    """
    return available_columns[0] if available_columns else 0
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over"):
            break

        board = info.get("board", [])
        player = info.get("current_player", "R")
        cols = info.get("available_columns", [])
        col = choose_column(board, player, cols)
        requests.post(f"{GAME_URL}/move", json={"column": col, "player": player}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // TIC_TAC_TOE
  // =========================================================================
  tic_tac_toe: {
    header: SHARED_HEADER,
    template: `def choose_move(board, current_player, available_moves):
    """
    Decide where to place your mark (X or O).

    Args:
        board (list):            3x3 grid of '' / 'X' / 'O'
        current_player (str):    'X' or 'O'
        available_moves (list):  [[row, col], ...] open cells

    Returns:
        tuple: (row, col) to mark
    """
    if available_moves:
        move = available_moves[0]
        return int(move[0]), int(move[1])
    return 0, 0
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over"):
            break

        board = info.get("board", [])
        player = info.get("current_player", "X")
        moves = info.get("available_moves", [])
        if not moves:
            break
        row, col = choose_move(board, player, moves)
        requests.post(f"{GAME_URL}/move", json={"row": row, "col": col, "player": player}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // ONE_ARMED_BANDIT
  // =========================================================================
  one_armed_bandit: {
    header: SHARED_HEADER,
    template: `def choose_bet(credits, spin_count):
    """
    Decide how much to bet on this spin.

    Args:
        credits (int):    Total credits available
        spin_count (int): Number of spins completed so far

    Returns:
        int: Amount to bet (0 to credits)
    """
    bet = 10
    return min(bet, credits) if credits > 0 else 0
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    spin_count = 0
    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over"):
            break

        credits = info.get("credits", 0)
        bet = choose_bet(credits, spin_count)
        if bet <= 0:
            break

        requests.post(f"{GAME_URL}/spin", json={"bet": bet}, timeout=5)
        spin_count += 1


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // PONG
  // =========================================================================
  pong: {
    header: SHARED_HEADER,
    template: `def choose_paddle_action(ball, paddles):
    """
    Decide paddle movement to intercept the ball.

    Args:
        ball (dict):     Ball state with keys x, y, vx, vy
        paddles (dict):  Paddle positions with keys player, opponent

    Returns:
        str: One of "up", "down", "stay"
    """
    ball_y = ball.get("y", 0.5)
    paddle_y = paddles.get("player", 0.5)
    if ball_y < paddle_y - 0.01:
        return "up"
    if ball_y > paddle_y + 0.01:
        return "down"
    return "stay"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over"):
            break

        ball = info.get("ball", {})
        paddles = info.get("paddles", {})
        action = choose_paddle_action(ball, paddles)
        requests.post(f"{GAME_URL}/step", json={"action": action}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // BLACKJACK
  // =========================================================================
  blackjack: {
    header: SHARED_HEADER,
    template: `def choose_action(player_hand, dealer_hand, bankroll, round_active):
    """
    Decide action for the current blackjack state.

    Args:
        player_hand (list):  Your cards (e.g., ["AH", "10S"])
        dealer_hand (list):  Dealer cards (upcard is dealer_hand[0])
        bankroll (int):      Total credits available
        round_active (bool): True during an active hand

    Returns:
        dict: {"action":"new_round","bet":10} or {"action":"hit"} or {"action":"stand"}
    """
    if not round_active:
        return {"action": "new_round", "bet": 10}
    return {"action": "stand"}
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over"):
            break

        player_hand = info.get("player_hand", [])
        dealer_hand = info.get("dealer_hand", [])
        bankroll = info.get("bankroll", 0)
        round_active = info.get("round_active", False)

        action_obj = choose_action(player_hand, dealer_hand, bankroll, round_active)
        requests.post(f"{GAME_URL}/step", json=action_obj, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // LIGHTS_OUT
  // =========================================================================
  lights_out: {
    header: SHARED_HEADER,
    template: `def choose_button(board, moves_left):
    """
    Decide which cell to press on the 5x5 grid.

    Args:
        board (list):      5x5 matrix where 0=off, 1=on
        moves_left (int):  Remaining moves before losing

    Returns:
        tuple: (row, col) to press
    """
    for row in range(len(board)):
        for col in range(len(board[row])):
            if board[row][col]:
                return (row, col)
    return (0, 0)
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_solved"):
            break

        board = info.get("board", [])
        max_moves = info.get("meta", {}).get("max_moves", 200)
        moves_made = info.get("moves", 0)
        moves_left = max_moves - moves_made
        row, col = choose_button(board, moves_left)

        requests.post(f"{GAME_URL}/move", json={"row": row, "col": col}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // MINESWEEPER
  // =========================================================================
  minesweeper: {
    header: SHARED_HEADER,
    template: `def choose_cell(visible_board, rows, cols):
    """
    Decide action: reveal or flag a cell.

    Args:
        visible_board (list): 9x9 grid of '?', 'F', 'M', '0'-'8'
        rows (int):            Board height
        cols (int):            Board width

    Returns:
        tuple: (action, row, col) where action is 'reveal' or 'flag'
    """
    for row in range(rows):
        for col in range(cols):
            if visible_board[row][col] == "?":
                return ("reveal", row, col)
    return ("reveal", 0, 0)
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over") or info.get("is_victory"):
            break

        visible = info.get("visible_board", [])
        rows = info.get("rows", 9)
        cols = info.get("cols", 9)
        action, row, col = choose_cell(visible, rows, cols)

        endpoint = "/reveal" if action == "reveal" else "/flag"
        requests.post(f"{GAME_URL}{endpoint}", json={"row": row, "col": col}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // NUMBERLINK
  // =========================================================================
  numberlink: {
    header: SHARED_HEADER,
    template: `def get_solution_paths():
    """
    Return hardcoded pair paths for the default 5x5 board.

    Returns:
        dict: pair id -> list of (row, col) cells to draw in order
    """
    return {
        "1": [(0, 0), (1, 0), (2, 0), (3, 0), (4, 0), (4, 1), (4, 2), (4, 3), (4, 4)],
        "3": [(1, 1), (0, 1), (0, 2), (0, 3), (0, 4), (1, 4), (2, 4), (3, 4), (3, 3)],
        "4": [(1, 3), (2, 3), (2, 2), (3, 2), (3, 1)],
    }
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    paths = get_solution_paths()
    for pair_id, cells in paths.items():
        for row, col in cells:
            requests.post(
                f"{GAME_URL}/draw",
                json={"pair": pair_id, "row": row, "col": col},
                timeout=5,
            )

    try:
        info = requests.get(f"{GAME_URL}/info", timeout=5).json()
    except Exception as e:
        print(f"info error: {e}")
        return

    if info.get("is_solved"):
        print("Puzzle solved")


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // PAC_MAN
  // =========================================================================
  pac_man: {
    header: SHARED_HEADER,
    template: `def choose_direction(pac_pos, ghosts, pellets, power_pellets):
    """
    Decide direction for Pac-Man to move.

    Args:
        pac_pos (list):         [row, col] Pac-Man position
        ghosts (list):          Ghost objects with position, mode, fright_timer
        pellets (list):         Remaining pellet positions
        power_pellets (list):   Remaining power pellet positions

    Returns:
        str: One of "up", "down", "left", "right"
    """
    return "right"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"Game loop error: {e}")
            break

        if info.get("is_game_over") or info.get("is_win"):
            break

        pac = info.get("pac", {})
        pac_pos = pac.get("position", [0, 0])
        ghosts = info.get("ghosts", [])
        pellets = info.get("pellets", [])
        power_pellets = info.get("power_pellets", [])

        direction = choose_direction(pac_pos, ghosts, pellets, power_pellets)
        requests.post(f"{GAME_URL}/move", json={"direction": direction}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // ROAD_FIGHTER
  // =========================================================================
  road_fighter: {
    header: SHARED_HEADER,
    template: `def choose_action(player, obstacles, fuel_packs=None, level=1, health_packs=None):
    """
    Decide driving action for the next tick.

    Args:
        player (dict):      Player state with lane, distance, speed, fuel, damage
        obstacles (list):   Obstacle objects with lane and distance (some weave on L3+)
        fuel_packs (list):  Optional refill cans with lane and distance
        level (int):        Current stage 1–5 (harder traffic / longer tracks)
        health_packs (list): Optional repair crates; collecting one reduces damage

    Returns:
        str: One of "accelerate", "brake", "lane_left", "lane_right"
             Hitting a road edge (lane 0 left / lane 2 right) causes a speed-scaled
             skid and damage — stay in the three lanes.
             Speed 0 is legal (full stop); braking is not clamped above 0.
             Empty fuel ends the race (is_finished / is_out_of_fuel), not a campaign win.
             Finishing a stage awards a fuel/repair/score bonus and holds
             level_clear on /info. The next tick or action starts the next level.
    """
    return "accelerate"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"Game loop error: {e}")
            break

        if info.get("is_finished") or info.get("is_crashed"):
            break

        if info.get("level_clear"):
            requests.post(f"{GAME_URL}/tick", timeout=5)
            continue

        player = info.get("player", {})
        obstacles = info.get("obstacles", [])
        fuel_packs = info.get("fuel_packs", [])
        health_packs = info.get("health_packs", [])
        level = info.get("level", 1)
        action = choose_action(player, obstacles, fuel_packs, level, health_packs)
        requests.post(f"{GAME_URL}/{action}", timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // SNAKE
  // =========================================================================
  snake: {
    header: SHARED_HEADER,
    template: `def get_move(snake, food, board_size):
    """
    Decide the next move for the snake.

    Args:
        snake (list):      list of [row, col] segments; snake[0] is the head
        food (list):       [row, col] position of the food
        board_size (int):  size of the square board

    Returns:
        str: one of "up", "down", "left", "right"
    """
    return "right"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    board_size = 10

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("game_over"):
            break

        snake = info.get("snake", [])
        food = info.get("food")
        move = get_move(snake, food, board_size)

        try:
            resp = requests.post(f"{GAME_URL}/move", json={"direction": move}, timeout=5)
        except Exception as e:
            print(f"move error: {e}")
            break

        if resp.json().get("game_over"):
            break


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // TWO_JOINT_POLE
  // =========================================================================
  two_joint_pole: {
    header: SHARED_HEADER,
    template: `def choose_action(physics):
    """
    Decide action to keep both poles off the ground.

    The episode ends only when a pole (joint or tip) reaches the ground,
    not at a small tilt angle.

    Args:
        physics (dict): cart_position, cart_velocity, pole1_angle, pole2_angle

    Returns:
        str: One of "left", "stay", "right"
    """
    pole1 = physics.get("pole1_angle", 0.0)
    if pole1 > 0.02:
        return "left"
    if pole1 < -0.02:
        return "right"
    return "stay"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_over"):
            break

        physics = info.get("physics", {})
        action = choose_action(physics)
        requests.post(f"{GAME_URL}/step", json={"action": action}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // PUZZLE_MASTER (SLIDING_TILES)
  // =========================================================================
  puzzle_master: {
    header: SHARED_HEADER,
    template: `def choose_position(puzzle):
    """
    Decide which tile position to slide into the empty space.

    Args:
        puzzle (list): Flat 4x4 board; empty cell is 999

    Returns:
        int: board index of the tile to move (must be adjacent to empty)
    """
    empty = puzzle.index(999)
    row, col = empty // 4, empty % 4
    candidates = []
    if row > 0:
        candidates.append(empty - 4)
    if row < 3:
        candidates.append(empty + 4)
    if col > 0:
        candidates.append(empty - 1)
    if col < 3:
        candidates.append(empty + 1)
    return candidates[0] if candidates else empty
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
        requests.post(f"{GAME_URL}/start", timeout=5)
    except Exception as e:
        print(f"Reset/start failed: {e}")
        return

    for _ in range(500):
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break

        if info.get("is_game_won"):
            break

        puzzle = info.get("puzzle", [])
        position = choose_position(puzzle)
        resp = requests.post(f"{GAME_URL}/input", json={"position": position}, timeout=5)
        if resp.status_code != 200:
            break


if __name__ == "__main__":
    auto_play()
`,
  },

  // =========================================================================
  // STACKFALL (internal key: tetris; canvas page — GM optional)
  // =========================================================================
  tetris: {
    header: SHARED_HEADER,
    template: `def choose_action(board, current_piece):
    """
    Decide a falling-block piece action.

    Returns:
        str: one of "left", "right", "rotate", "drop"
    """
    return "drop"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return

    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break
        if info.get("is_game_over"):
            break
        action = choose_action(info.get("board", []), info.get("current_piece"))
        requests.post(f"{GAME_URL}/step", json={"action": action}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  adventure_quest: {
    header: SHARED_HEADER,
    template: `def choose_action(player, enemies, treasures):
    """Return one of up, down, left, right."""
    return "right"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return
    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break
        if info.get("is_game_over"):
            break
        action = choose_action(info.get("player"), info.get("enemies"), info.get("treasures"))
        requests.post(f"{GAME_URL}/step", json={"action": action}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  space_invader: {
    header: SHARED_HEADER,
    template: `def choose_action(player_x, invaders):
    """Return left, right, or fire."""
    return "fire"
`,
    footer: `def auto_play():
    try:
        requests.post(f"{GAME_URL}/reset", timeout=5)
    except Exception as e:
        print(f"Reset failed: {e}")
        return
    while True:
        try:
            info = requests.get(f"{GAME_URL}/info", timeout=5).json()
        except Exception as e:
            print(f"info error: {e}")
            break
        if info.get("is_game_over"):
            break
        action = choose_action(info.get("player_x"), info.get("invaders"))
        requests.post(f"{GAME_URL}/step", json={"action": action}, timeout=5)


if __name__ == "__main__":
    auto_play()
`,
  },

  santa_drop: {
    header: SHARED_HEADER,
    template: `def scroll_ahead(tick, ticks, schedule):
    """How far the rooftops scroll during the given number of ticks from tick on."""
    total = 0
    for t in range(tick, tick + max(0, ticks)):
        speed = schedule[0][1]
        for start, value in schedule:
            if t >= start:
                speed = value
        total += speed
    return total


def choose_action(state):
    """
    Aim at the nearest chimney the sled can still reach.

    A gift falls straight down for meta.fall_ticks ticks while the houses keep
    scrolling left, so it has to be released ahead of the chimney. For every
    undelivered chimney that no gift in the air is already heading for, find
    the earliest tick at which the sled can be over the release point, steer
    towards it and drop once the sled is close enough.

    Returns:
        str: "left", "right", "drop" or "stay"
    """
    meta = state.get("meta") or {}
    sled = state.get("sled_pos", 0)  # field units; sled_x is the legacy lane 0-4
    tick = state.get("tick", 0)
    fall = meta.get("fall_ticks", 14)
    sled_speed = meta.get("sled_speed", 5)
    schedule = meta.get("speed_schedule") or [[0, 2]]
    lo = meta.get("sled_min_x", 8)
    hi = meta.get("sled_max_x", 170)
    if state.get("gifts_left", 1) <= 0:
        return "stay"

    houses = [h for h in state.get("houses") or [] if not h.get("delivered")]
    gifts = state.get("gifts_in_air") or []

    def claimed(house):
        for g in gifts:
            cx = house["chimney_x"] - scroll_ahead(tick, g.get("ticks_left", 0), schedule)
            if abs(g["x"] - cx) * 2 <= house["chimney_width"]:
                return True
        return False

    best = None  # (ticks until release, house)
    for house in houses:
        if claimed(house):
            continue
        for k in range(0, 60):
            release_x = (house["chimney_x"] - scroll_ahead(tick, k, schedule)
                         - scroll_ahead(tick + k, fall, schedule))
            if release_x < lo:
                break
            if release_x <= hi and abs(release_x - sled) <= sled_speed * k + 1:
                if best is None or k < best[0]:
                    best = (k, house)
                break
    if best is None:
        return "stay"

    house = best[1]
    drop_x = house["chimney_x"] - scroll_ahead(tick, fall, schedule)
    if abs(sled - drop_x) * 2 <= house["chimney_width"] - 4 and state.get("cooldown", 0) == 0:
        return "drop"
    next_x = (house["chimney_x"] - scroll_ahead(tick, 1, schedule)
              - scroll_ahead(tick + 1, fall, schedule))
    options = {
        "stay": sled,
        "left": max(lo, sled - sled_speed),
        "right": min(hi, sled + sled_speed),
    }
    return min(options, key=lambda a: abs(options[a] - next_x))
`,
    footer: `def auto_play():
    try:
        state = requests.post(f"{GAME_URL}/reset", timeout=5).json()
    except Exception as e:
        print(f"Reset failed: {e}")
        return
    while not state.get("is_game_over"):
        action = choose_action(state)
        try:
            resp = requests.post(f"{GAME_URL}/step", json={"action": action}, timeout=5)
        except Exception as e:
            print(f"step error: {e}")
            break
        if resp.status_code != 200:
            break
        state = resp.json()
    print(f"Santa Drop finished: score={state.get('score')} landed={state.get('gifts_landed')}/{state.get('gifts_dropped')}")


if __name__ == "__main__":
    auto_play()
`,
  },
};

export { GAME_BOILERPLATE, SHARED_HEADER };
